import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconSearch, IconChevronDown } from '@tabler/icons-react';
import { Text } from '@/components/ui/core';
import { Box, Stack } from '@/components/ui/layout';
import { Menu, Tooltip } from '@/components/ui/overlays';
import { ActionIcon } from '@/components/ui';
import { TextInput } from '@/components/ui/inputs';
import { useDomainAccess } from '@/hooks/useDomainAccess';

export default function DomainSearchButton() {
  const navigate = useNavigate();
  const { accessibleDomains } = useDomainAccess();
  const [selectedDomainKey, setSelectedDomainKey] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [domainMenuOpened, setDomainMenuOpened] = useState(false);
  const [resultsOpened, setResultsOpened] = useState(false);

  // Filter out user apps (Profile, Settings) - they are not searchable
  const searchableDomains = useMemo(() => {
    return accessibleDomains.filter(
      (domain) => !['profile', 'settings'].includes(domain.key as string)
    );
  }, [accessibleDomains]);

  // Get selected domain object
  const selectedDomain = useMemo(() => {
    return searchableDomains.find((d) => d.key === selectedDomainKey);
  }, [searchableDomains, selectedDomainKey]);

  // Mock search results - Replace with actual API call based on selected domain
  const searchResults = useMemo(() => {
    if (!selectedDomain || !searchQuery.trim()) {
      return [];
    }

    // TODO: Replace this with actual search API call for the selected domain
    // Example: await api.search(selectedDomain.key, searchQuery)

    // Mock results for demonstration
    const mockResults = [
      {
        id: '1',
        title: `${searchQuery} in ${selectedDomain.label}`,
        description: 'This is a mock search result',
        path: `${selectedDomain.basePath}/search?q=${encodeURIComponent(searchQuery)}`,
      },
      {
        id: '2',
        title: `Another result for ${searchQuery}`,
        description: 'Another mock search result',
        path: `${selectedDomain.basePath}/item/123`,
      },
    ];

    return mockResults;
  }, [selectedDomain, searchQuery]);

  const handleDomainSelect = (domainKey: string) => {
    setSelectedDomainKey(domainKey);
    setDomainMenuOpened(false);
    setSearchQuery('');
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    // Open results dropdown if we have a query and selected domain
    if (value.trim() && selectedDomain) {
      setResultsOpened(true);
    } else {
      setResultsOpened(false);
    }
  };

  const handleResultClick = (path: string) => {
    navigate(path);
    setResultsOpened(false);
    setSearchQuery('');
  };

  if (searchableDomains.length === 0) {
    return null; // Don't show if user has no searchable apps
  }

  return (
    <Box>
      <Box
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: 400,
        }}
      >
        {/* Grouped Search Control */}
        <Box
          style={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            border: '1px solid var(--mantine-color-gray-4)',
            borderRadius: 'var(--mantine-radius-sm)',
            backgroundColor: 'var(--mantine-color-body)',
            overflow: 'hidden',
          }}
        >
          {/* Domain Selector (Left) */}
          <Menu
            shadow="md"
            width={250}
            position="bottom-start"
            offset={4}
            opened={domainMenuOpened}
            onChange={setDomainMenuOpened}
          >
            <Menu.Target>
              <Tooltip label={selectedDomain?.label || 'Select app'} position="bottom">
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={() => setDomainMenuOpened(!domainMenuOpened)}
                  style={{
                    borderRadius: 0,
                    borderRight: '1px solid var(--mantine-color-gray-4)',
                  }}
                >
                  {selectedDomain ? (
                    <Box style={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {selectedDomain.icon}
                    </Box>
                  ) : (
                    <IconChevronDown size={16} />
                  )}
                </ActionIcon>
              </Tooltip>
            </Menu.Target>

            <Menu.Dropdown>
              {searchableDomains.map((domain) => (
                <Menu.Item
                  key={domain.key}
                  leftSection={
                    <Box style={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {domain.icon}
                    </Box>
                  }
                  onClick={() => handleDomainSelect(domain.key)}
                >
                  <Text size="sm">{domain.label}</Text>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>

          {/* Search Input (Right) */}
          <Menu
            shadow="md"
            width={400}
            position="bottom-end"
            offset={8}
            opened={resultsOpened}
            onChange={setResultsOpened}
          >
            <Menu.Target>
              <Box style={{ flex: 1, position: 'relative' }}>
                <TextInput
                  placeholder={
                    selectedDomain
                      ? `Search ${selectedDomain.label.toLowerCase()}...`
                      : 'Select an app first...'
                  }
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.currentTarget.value)}
                  leftSection={<IconSearch size={16} />}
                  size="sm"
                  disabled={!selectedDomain}
                  gradientFocus={false}
                  onFocus={() => {
                    if (searchQuery.trim() && selectedDomain) {
                      setResultsOpened(true);
                    }
                  }}
                  styles={{
                    input: {
                      border: 'none',
                      borderRadius: 0,
                    },
                  }}
                />
              </Box>
            </Menu.Target>

            <Menu.Dropdown>
              {searchResults.length > 0 ? (
                <Stack gap={0}>
                  {searchResults.map((result) => (
                    <Menu.Item
                      key={result.id}
                      onClick={() => handleResultClick(result.path)}
                    >
                      <Box>
                        <Text size="sm" fw={500}>
                          {result.title}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {result.description}
                        </Text>
                      </Box>
                    </Menu.Item>
                  ))}
                </Stack>
              ) : (
                <Box p="md" style={{ textAlign: 'center' }}>
                  <Text size="sm" c="dimmed">
                    No results found for "{searchQuery}"
                  </Text>
                </Box>
              )}
            </Menu.Dropdown>
          </Menu>
        </Box>
      </Box>
    </Box>
  );
}
